import { useEffect, useRef, useState } from 'react';
import { Copy, Trash2, Plus, GripVertical, Move, Keyboard, Sparkles } from 'lucide-react';
import { useShallow } from 'zustand/react/shallow';
import { useToleranceStore } from '../../stores/toleranceStore';
import { useAuthStore, selectIsFree } from '../../stores/authStore';
import { getDeviations, isOverriddenFromNorm, isNormOutOfRange } from '../../utils/elementShape';
import TextInput from './measure/TextInput';
import NumInput from './measure/NumInput';
import DirToggle from './measure/DirToggle';
import DistChip from './measure/DistChip';
import NormCell from './measure/NormCell';
import TolCell from './measure/TolCell';
import MaterialCell from './measure/MaterialCell';
import DensitySwitch from './measure/DensitySwitch';
import { fmt } from './measure/numFormat';
import WissenHinweis from './WissenHinweis';

// MeasureTable — replaces the old Toolbox + ChainDisplay edit modal as
// the single editing surface for the chain. Inline-editable grid with
// per-row Norm + Distribution + smart-parsed tolerance + asymmetric
// mode toggle. Pro-only columns (Distribution) hide for Free.
//
// Layout: flex-based rows (not <table>) so cell heights track the
// density var and inputs can render across two stacked lines (the
// asymmetric tolerance editor). Column widths are pinned so the grid
// stays aligned across rows; the only flexible column is "Bezeichnung".

const selectChainState = (s) => ({
  elements: s.elements,
  addElement: s.addElement,
  insertElementAfter: s.insertElementAfter,
  updateElement: s.updateElement,
  removeElement: s.removeElement,
  reorderElements: s.reorderElements,
  structureLocked: s.structureLocked,
  preview: s.preview,
  // thermal-Block aus der Berechnung: steuert die Sichtbarkeit der
  // Material-Spalte. Die Spalte erscheint nur wenn der User
  // Wärmeausdehnung aktiviert hat — sonst kein Clutter für die ~80 %
  // der Anwender, die das Feature nicht brauchen.
  thermalEnabled: s.thermal?.enabled === true,
  setElementMaterial: s.setElementMaterial,
  setElementActive: s.setElementActive,
});

// ─── Cell wrappers ─────────────────────────────────────────────────

function HeaderCell({ children, width, align = 'left' }) {
  return (
    <div
      className="text-ink-3 bg-surface-2 border-r border-line"
      style={{
        width, flex: width ? 'none' : 1,
        minWidth: width || 0,
        height: 36,
        display: 'flex', alignItems: 'center', justifyContent: align === 'right' ? 'flex-end' : align === 'center' ? 'center' : 'flex-start',
        gap: 4,
        padding: '0 12px',
        fontSize: 10.5, fontWeight: 600, letterSpacing: 0.6,
        textTransform: 'uppercase',
      }}
    >
      {children}
    </div>
  );
}

function Cell({ children, width, padded = true, align = 'left' }) {
  return (
    <div
      className="border-r border-line"
      style={{
        width, flex: width ? 'none' : 1,
        minWidth: width || 0,
        height: 'var(--row-h)',
        display: 'flex', alignItems: 'center',
        justifyContent: align === 'right' ? 'flex-end' : align === 'center' ? 'center' : 'flex-start',
        padding: padded ? '0 12px' : 0,
      }}
    >
      {children}
    </div>
  );
}

// ─── Row ───────────────────────────────────────────────────────────

function Row({
  row, idx, isFree, structureLocked,
  thermalEnabled, onMaterialChange, onActiveChange,
  onUpdate, onDelete, onDuplicate, onAddAfter,
  autoFocusName,
  dragOverEdge, onDragStart, onDragOver, onDragLeave, onDrop, onDragEnd, isDragging,
}) {
  // Auto-focus the Bezeichnung input when the parent flags this row
  // as the freshly-inserted one (Enter-in-tol-cell flow). Runs once on
  // the prop flipping to true; subsequent renders are no-ops since the
  // ref is already focused.
  const nameRef = useRef(null);
  useEffect(() => {
    if (autoFocusName) {
      nameRef.current?.focus();
      nameRef.current?.select?.();
    }
  }, [autoFocusName]);

  // Drag-handle gating: HTML5 DnD's `draggable` attr applies to the
  // whole element, so a permissive `draggable={true}` on the row turns
  // every text input into a drag source — try-to-select-text drags the
  // row instead. We keep the row non-draggable by default and "arm"
  // it briefly when the user mousedowns on the GripVertical handle.
  // Disarm fires on mouseup OR dragend so we never get stuck armed.
  const [dragArmed, setDragArmed] = useState(false);
  const armDrag   = () => setDragArmed(true);
  const disarmDrag = () => setDragArmed(false);
  const { lower, upper } = getDeviations(row);
  const nominal = Number(row.nominal) || 0;
  const dir = row.direction === -1 ? -1 : 1;
  // MIN/MAX are the element's signed contribution to the closure. The
  // direction column already carries the +/− sign visually, so the
  // numeric column shows magnitude only per user feedback ("ohne
  // Vorzeichen"). For symmetric tolerances the values are near-equal
  // magnitude either side of the nominal; for unidirektional or
  // asymmetric pairs, MIN and MAX visibly differ.
  const minVraw = nominal * dir + (dir === 1 ? lower : -upper);
  const maxVraw = nominal * dir + (dir === 1 ? upper : -lower);
  const showRange = Number.isFinite(nominal) && (lower !== 0 || upper !== 0);

  // Norm overridden cue: row carries `norm` but the stored tol values
  // diverge from what the norm formula would produce at this nominal.
  // Cleared (zero) values restore the norm via toleranceStore's
  // clear-to-norm path, so we don't need to special-case empty here.
  const overridden = isOverriddenFromNorm(row);

  const disabled = structureLocked;

  return (
    <div
      className="border-b border-line group hover:bg-surface-2 focus-within:bg-brand-soft/30 relative"
      style={{
        display: 'flex',
        opacity: isDragging ? 0.4 : row.inactive ? 0.55 : 1,
        transition: 'opacity 120ms ease, background-color 120ms ease',
      }}
      data-inactive={row.inactive ? 'true' : undefined}
      draggable={dragArmed && !disabled}
      onDragStart={(e) => onDragStart(e, idx)}
      onDragOver={(e) => onDragOver(e, idx)}
      onDragLeave={onDragLeave}
      onDrop={(e) => onDrop(e, idx)}
      onDragEnd={(e) => { disarmDrag(); onDragEnd?.(e); }}
    >
      {/* Drop-target indicator. dragOverEdge: 'top' or 'bottom' means
          the dragged row would land here; a thin brand line is drawn
          on the appropriate edge so the drop position is obvious. */}
      {dragOverEdge && (
        <div
          aria-hidden="true"
          className="bg-brand"
          style={{
            position: 'absolute',
            left: 0, right: 0,
            [dragOverEdge]: -1,
            height: 2,
            zIndex: 2,
            pointerEvents: 'none',
          }}
        />
      )}
      <Cell width={26} padded={false} align="center">
        <span
          role="button"
          aria-label="Zeile per Drag-and-Drop verschieben"
          title="Ziehen zum Umordnen"
          onMouseDown={disabled ? undefined : armDrag}
          onMouseUp={disarmDrag}
          onMouseLeave={disarmDrag}
          style={{
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            width: 22, height: 22,
            cursor: disabled ? 'not-allowed' : (dragArmed ? 'grabbing' : 'grab'),
            touchAction: 'none',
          }}
        >
          <GripVertical
            size={13}
            className={`text-ink-4 ${disabled ? 'opacity-30' : 'opacity-40 group-hover:opacity-100'}`}
            aria-hidden="true"
          />
        </span>
      </Cell>
      <Cell width={36} align="center">
        <span className="num text-ink-3" style={{ fontSize: 11, fontWeight: 600 }}>
          M<sub style={{ fontSize: 9 }}>{idx + 1}</sub>
        </span>
      </Cell>
      <Cell>
        <TextInput
          value={row.name}
          onCommit={(v) => onUpdate({ name: v })}
          placeholder="z.B. Gehäuselänge"
          ariaLabel="Bezeichnung"
          strike={row.inactive}
          onEnter={onAddAfter}
          inputRef={nameRef}
        />
      </Cell>
      <Cell width={102} align="right">
        <NumInput
          value={row.nominal}
          onCommit={(raw) => {
            const n = parseFloat(String(raw).replace(',', '.'));
            if (!Number.isFinite(n)) { onUpdate({ nominal: 0 }); return; }
            // Sign of the typed value drives the Wirkrichtung — the
            // Nennmaß column itself only ever stores positive numbers
            // so the table reads cleanly. A leading minus is just a
            // shortcut for "flip Richtung to −".
            if (n < 0) onUpdate({ nominal: -n, direction: -1 });
            else        onUpdate({ nominal: n });
          }}
          placeholder="0,000"
          align="right"
          ariaLabel="Nennmaß"
        />
      </Cell>
      <Cell width={84} align="center" padded={false}>
        <DirToggle
          direction={dir}
          inactive={row.inactive}
          onChange={({ direction, inactive }) => {
            if (inactive) onActiveChange(false);
            else onUpdate({ direction, inactive: false });
          }}
        />
      </Cell>
      <Cell width={146}>
        <TolCell row={row} onCommit={onUpdate} onEnter={onAddAfter} overridden={overridden} isFree={isFree} />
      </Cell>
      <Cell width={180} padded={false}>
        <div style={{ padding: '0 8px', width: '100%' }}>
          <NormCell
            value={row.norm || ''}
            overridden={overridden}
            outOfRange={isNormOutOfRange(row)}
            isFree={isFree}
            onChange={(v) => {
              // Just patch `norm`. The store's updateElement detects
              // the norm change (without any touched-tol fields) and
              // runs refreshFromNorm to derive the proper deviations
              // from the norm formula at the row's current nominal —
              // sym vs. asym storage is picked automatically by
              // fromDeviations based on what the norm returned.
              onUpdate({ norm: v });
            }}
          />
        </div>
      </Cell>
      {!isFree && (
        <Cell width={120} padded={false}>
          <div style={{ padding: '0 8px' }}>
            <DistChip value={row.distribution} cpk={row.cpk} onUpdate={onUpdate} />
          </div>
        </Cell>
      )}
      {thermalEnabled && (
        <Cell width={150} padded={false}>
          <div style={{ padding: '0 8px', width: '100%' }}>
            <MaterialCell
              value={row.material || ''}
              onChange={onMaterialChange}
              // Free-User können thermische Demos öffnen (z.B. via
              // Template) und sollen die Werte sehen, aber das Material
              // nicht ändern können — Pro-Feature.
              readOnly={isFree}
            />
          </div>
        </Cell>
      )}
      <Cell width={110} align="right">
        {row.inactive ? (
          <span className="text-ink-4" style={{ fontSize: 11 }}>inaktiv</span>
        ) : showRange ? (
          <div className="num text-ink-3" style={{ fontSize: 11, lineHeight: 1.3, display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
            <div style={{ display: 'flex', gap: 5, alignItems: 'center' }}>
              <span className="text-ink-4" style={{ fontSize: 9, minWidth: 22 }}>MIN</span>
              <span>{fmt(Math.abs(minVraw), 3)}</span>
            </div>
            <div style={{ display: 'flex', gap: 5, alignItems: 'center' }}>
              <span className="text-ink-4" style={{ fontSize: 9, minWidth: 22 }}>MAX</span>
              <span className="text-ink-2">{fmt(Math.abs(maxVraw), 3)}</span>
            </div>
          </div>
        ) : (
          <span className="text-ink-4">-</span>
        )}
      </Cell>
      {/* Row actions: duplicate + delete only. Up/down arrows are gone
          per user feedback — drag-to-reorder covers the same need and
          the redundant buttons cluttered the column. tabIndex={-1} on
          both so keyboard Tab walks past them straight to the next
          row's editable cells. */}
      <div
        className="border-r-0 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity"
        style={{ width: 56, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2 }}
      >
        <button
          type="button"
          tabIndex={-1}
          onClick={() => onDuplicate(row)}
          disabled={disabled}
          title="Duplizieren"
          className="text-ink-3 hover:text-brand disabled:opacity-30"
          style={{ width: 22, height: 22, border: 'none', background: 'transparent', borderRadius: 4, cursor: disabled ? 'not-allowed' : 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
        >
          <Copy size={12} aria-hidden="true" />
        </button>
        <button
          type="button"
          tabIndex={-1}
          onClick={() => onDelete(row.id)}
          disabled={disabled}
          title="Löschen"
          className="text-ink-3 hover:text-err disabled:opacity-30"
          style={{ width: 22, height: 22, border: 'none', background: 'transparent', borderRadius: 4, cursor: disabled ? 'not-allowed' : 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
        >
          <Trash2 size={12} aria-hidden="true" />
        </button>
      </div>
    </div>
  );
}

// ─── Quick-add (always-last) row ───────────────────────────────────

// Empty-state CTA panel — replaces the (now removed) three sample
// rows with a friendly onboarding hint when the chain is empty. Two
// equally-prominent buttons: a primary "Maß hinzufügen" (solid brand)
// and a secondary "Vorlage wählen" (brand-soft) that dispatches the
// custom event AppHeader listens for. Replaces the earlier arrow +
// text combo — users intuitively click buttons, not type-styled hints.
function EmptyChainHint({ onAdd }) {
  const openTemplates = () => {
    window.dispatchEvent(new Event('toleranz:open-templates'));
  };
  const primaryBtn = {
    fontSize: 12, fontWeight: 600,
    padding: '7px 14px', borderRadius: 6,
    border: 'none', cursor: 'pointer',
  };
  return (
    <div
      className="bg-surface text-ink-3"
      style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        gap: 6, padding: '28px 16px 24px', borderBottom: '1px dashed var(--color-line)',
      }}
    >
      <div className="text-ink-2" style={{ fontSize: 14, fontWeight: 600 }}>
        Beginne deine Toleranzkette
      </div>
      <div className="text-ink-3" style={{ fontSize: 12, maxWidth: 360, textAlign: 'center', lineHeight: 1.5 }}>
        Füge das erste Maß hinzu oder starte mit einer Vorlage.
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 10 }}>
        <button
          type="button"
          onClick={onAdd}
          className="inline-flex items-center gap-1.5 bg-brand text-white hover:bg-[var(--color-brand-2)] transition-colors shadow-sm"
          style={primaryBtn}
        >
          <Plus size={13} aria-hidden="true" />
          Maß hinzufügen
        </button>
        <button
          type="button"
          onClick={openTemplates}
          className="inline-flex items-center gap-1.5 bg-brand-soft text-brand hover:bg-brand hover:text-white transition-colors"
          style={primaryBtn}
        >
          <Sparkles size={13} aria-hidden="true" />
          Vorlage wählen
        </button>
      </div>
    </div>
  );
}

function QuickAddRow({ structureLocked, onAdd }) {
  if (structureLocked) return null;
  return (
    <button
      type="button"
      onClick={onAdd}
      className="text-ink-3 hover:bg-surface-2 transition-colors w-full"
      style={{
        display: 'flex', alignItems: 'center', gap: 10,
        height: 'var(--row-h)',
        border: 'none', background: 'transparent',
        padding: '0 12px',
        borderBottom: '1px dashed var(--color-line)',
        cursor: 'pointer',
      }}
    >
      <span
        className="bg-brand-soft text-brand"
        style={{ width: 18, height: 18, borderRadius: 4, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginLeft: 11 }}
      >
        <Plus size={12} aria-hidden="true" />
      </span>
      <span style={{ fontSize: 13, fontWeight: 500 }}>
        Maß hinzufügen
      </span>
      <span
        className="text-ink-4"
        style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 6, fontSize: 11 }}
      >
        <kbd className="bg-surface text-ink-2" style={{ ...kbdStyle, border: '1px solid var(--color-line-strong)', borderBottomWidth: 2 }}>↵</kbd>
        <span>im Toleranzfeld fügt ebenfalls hinzu</span>
      </span>
    </button>
  );
}

// ─── MeasureTable (main) ───────────────────────────────────────────

export default function MeasureTable() {
  const {
    elements, addElement, insertElementAfter, updateElement, removeElement, reorderElements,
    structureLocked, preview,
    thermalEnabled, setElementMaterial, setElementActive,
  } = useToleranceStore(useShallow(selectChainState));
  const inactiveCount = elements.filter((el) => el.inactive).length;
  const isFree = useAuthStore(selectIsFree);

  // Tracks which row should auto-focus its name input after mount.
  // Set by the Enter-in-tol-cell flow; consumed once by the matching
  // Row's useEffect, then left in place (autoFocus is a one-shot per
  // mount — staying truthy on later renders has no effect).
  const [pendingFocusId, setPendingFocusId] = useState(null);

  // ── Drag-to-reorder state ─────────────────────────────────────
  // HTML5 native DnD: source index in `dragFromIdx`, target row in
  // `dragOverIdx`, half-of-row in `dragOverEdge` so the drop bar
  // renders on the correct edge. All cleared on drop / dragend.
  const [dragFromIdx, setDragFromIdx] = useState(null);
  const [dragOverIdx, setDragOverIdx] = useState(null);
  const [dragOverEdge, setDragOverEdge] = useState(null);

  const handleDragStart = (e, idx) => {
    setDragFromIdx(idx);
    // Required so Firefox emits drop events.
    try { e.dataTransfer.setData('text/plain', String(idx)); } catch { /* some browsers throw on read-only ops in unit tests */ }
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, idx) => {
    if (dragFromIdx == null || dragFromIdx === idx) return;
    e.preventDefault(); // required to make this a valid drop target
    e.dataTransfer.dropEffect = 'move';
    const rect = e.currentTarget.getBoundingClientRect();
    const edge = (e.clientY - rect.top) < rect.height / 2 ? 'top' : 'bottom';
    if (dragOverIdx !== idx || dragOverEdge !== edge) {
      setDragOverIdx(idx);
      setDragOverEdge(edge);
    }
  };

  const handleDrop = (e, idx) => {
    e.preventDefault();
    if (dragFromIdx == null || dragFromIdx === idx) {
      clearDrag();
      return;
    }
    // Compute the target index: 'top' = insert before, 'bottom' = after.
    // When dragging down past the original position we shift one because
    // the source slot collapses; the store's reorderElements handles
    // the splice itself, but it expects the *final* index, not the
    // visual-edge index.
    let toIdx = dragOverEdge === 'bottom' ? idx + 1 : idx;
    if (dragFromIdx < toIdx) toIdx -= 1;
    if (toIdx !== dragFromIdx) reorderElements(dragFromIdx, toIdx);
    clearDrag();
  };

  const clearDrag = () => {
    setDragFromIdx(null);
    setDragOverIdx(null);
    setDragOverEdge(null);
  };

  const blankElement = () => ({
    name: '', nominal: 0, direction: 1,
    tolMode: 'symmetric', tol: 0, tolUpper: 0, tolLower: 0,
    distribution: 'normal',
    norm: '',
  });

  const handleAdd = () => {
    if (structureLocked) return;
    const newId = addElement(blankElement());
    if (newId) setPendingFocusId(newId);
  };

  // Enter inside the tol cell on row X → insert blank row after X and
  // pull focus into the new row's Bezeichnung input. Mirrors the
  // spreadsheet-style "Enter creates next row" pattern users expect
  // when filling a table top-to-bottom.
  const handleAddAfter = (rowId) => {
    if (structureLocked) return;
    const newId = insertElementAfter(rowId, blankElement());
    if (newId) setPendingFocusId(newId);
  };

  const handleDuplicate = (row) => {
    if (structureLocked) return;
    // makeElement assigns a fresh id; spread copies the rest.
    addElement({ ...row, name: row.name ? `${row.name} (Kopie)` : '', id: undefined });
  };

  return (
    <>
      {/* Section heading — mirrors MassketteDiagram's heading-card
          pattern. Tagline explains the inline-edit affordance + sits
          flush with the DensitySwitch which controls the table's
          row height. */}
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, paddingLeft: 4, flexWrap: 'wrap', rowGap: 6 }}>
        <h2
          className="text-ink-2"
          style={{ fontSize: 13, fontWeight: 600, margin: 0, letterSpacing: 0.2, textTransform: 'uppercase' }}
        >
          Maßtabelle &amp; Reihenfolge
        </h2>
        <span className="text-ink-3" style={{ fontSize: 11 }}>
          Tippen Sie direkt in jede Zelle - die Berechnung läuft live.
        </span>
        {inactiveCount > 0 && (
          <span className="text-warn" style={{ fontSize: 11, fontWeight: 600 }}>
            {inactiveCount === 1 ? '1 Maß inaktiv' : `${inactiveCount} Maße inaktiv`}
          </span>
        )}
        <span style={{ flex: 1 }} />
        <DensitySwitch />
      </div>

      <div
        data-tour="measure-table"
        className="bg-surface border border-line"
        style={{
          borderRadius: 'var(--radius-lg)',
          overflow: 'hidden',
          display: 'flex', flexDirection: 'column',
          flex: 1, minHeight: 0,
        }}
      >
        <div style={{ flex: 1, overflowX: 'auto', overflowY: 'auto', minHeight: 0 }}>
          <div style={{ minWidth: 'fit-content' }}>
          {/* Sticky header */}
          <div style={{ display: 'flex', borderBottom: '1px solid var(--color-line)' }}>
            <HeaderCell width={26} />
            <HeaderCell width={36} align="center">#</HeaderCell>
            <HeaderCell>Bezeichnung</HeaderCell>
            <HeaderCell width={102} align="right">Nennmaß</HeaderCell>
            <HeaderCell width={84} align="center">Richt. <WissenHinweis id="richtung" /></HeaderCell>
            <HeaderCell width={146}>Toleranz</HeaderCell>
            <HeaderCell width={180}>Norm <WissenHinweis id="norm" /></HeaderCell>
            {!isFree && <HeaderCell width={120}>Verteilung <WissenHinweis id="verteilung" /></HeaderCell>}
            {thermalEnabled && <HeaderCell width={150}>Material <WissenHinweis id="material" /></HeaderCell>}
            <HeaderCell width={110} align="right">Min / Max</HeaderCell>
            <HeaderCell width={56} align="center"> </HeaderCell>
          </div>

          {/* Versions-Vorschau: ein deaktiviertes fieldset sperrt alle
              Eingaben und Knöpfe darin nativ; display: contents lässt
              das Layout unberührt. */}
          <fieldset data-preview-lock disabled={!!preview} style={{ display: 'contents' }}>
            {/* Empty-state hint replaces the previously-shipped sample
                rows. Only renders when the chain is truly empty AND the
                structure isn't locked (locked = loaded saved calc with
                no items, an edge case we don't want to nudge). */}
            {elements.length === 0 && !structureLocked && <EmptyChainHint onAdd={handleAdd} />}

            {/* Rows */}
            {elements.map((row, idx) => (
              <Row
                key={row.id}
                row={row}
                idx={idx}
                isFree={isFree}
                structureLocked={structureLocked}
                thermalEnabled={thermalEnabled}
                onMaterialChange={(materialId) => setElementMaterial(row.id, materialId)}
              onActiveChange={(active) => setElementActive(row.id, active)}
                onUpdate={(patch) => updateElement(row.id, patch)}
                onDelete={removeElement}
                onDuplicate={handleDuplicate}
                onAddAfter={() => handleAddAfter(row.id)}
                autoFocusName={pendingFocusId === row.id}
                isDragging={dragFromIdx === idx}
                dragOverEdge={dragOverIdx === idx ? dragOverEdge : null}
                onDragStart={handleDragStart}
                onDragOver={handleDragOver}
                onDragLeave={() => { /* row-level leave is handled implicitly when another row dragOvers */ }}
                onDrop={handleDrop}
                onDragEnd={clearDrag}
              />
            ))}

            <QuickAddRow structureLocked={structureLocked} onAdd={handleAdd} />
          </fieldset>

          {/* Footer hints — drag affordance + keyboard cheatsheet */}
          <div
            className="bg-surface-2 text-ink-3"
            style={{
              display: 'flex', alignItems: 'center',
              height: 'var(--row-h)',
              padding: '0 12px',
              fontSize: 11,
            }}
          >
            <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <Move size={13} aria-hidden="true" />
              Ziehen zum Umordnen
            </span>
            <span className="text-ink-4 mx-3">·</span>
            <span>
              {elements.length} {elements.length === 1 ? 'Maß' : 'Maße'}
              {structureLocked && ' · Struktur gesperrt'}
            </span>
            <span style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 14 }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <Keyboard size={13} aria-hidden="true" />
                <span>
                  <kbd className="bg-surface border border-line text-ink-2" style={kbdStyle}>Tab</kbd> nächstes Feld
                </span>
              </span>
              <span>
                <kbd className="bg-surface border border-line text-ink-2" style={kbdStyle}>↵</kbd> neue Zeile
              </span>
            </span>
          </div>
          </div>
        </div>
      </div>
    </>
  );
}

// kbd visual tokens lifted out to a constant since we render several in
// the footer. Same look as the SubBar / palette kbd chips.
const kbdStyle = {
  padding: '1px 4px',
  borderRadius: 3,
  fontFamily: 'var(--font-mono)',
  fontSize: 10,
};
